/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequestPatientSummaryProps {
  id: string;
  dateOfBirth: Date | null;
  weightInKg: number | null;
  dateCreated: Date | null;
}

export class RequestPatientSummaryRecord extends Record<RequestPatientSummaryProps>({
  id: null as any,
  dateOfBirth: null as any,
  weightInKg: null as any,
  dateCreated: null as any,
}) {
  public static fromJS(json: {
    id: string;
    dateOfBirth: string | number | Date | null;
    weightInKg: number | null;
    dateCreated: string | number | Date | null;
  }): RequestPatientSummaryRecord {
    if (json == null) {
      throw new Error('RequestPatientSummaryRecord.fromJS: parameter cannot be null');
    }
    return new RequestPatientSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequestPatientSummaryJson.id',
      ),
      dateOfBirth: json.dateOfBirth != null ? new Date(json.dateOfBirth) : null,
      weightInKg: json.weightInKg,
      dateCreated: json.dateCreated != null ? new Date(json.dateCreated) : null,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    dateOfBirth: string | number | Date | null;
    weightInKg: number | null;
    dateCreated: string | number | Date | null;
  }): [RequestPatientSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('RequestPatientSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequestPatientSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequestPatientSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOfBirth: tryOrDefault(
        () => (json.dateOfBirth != null ? new Date(json.dateOfBirth) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weightInKg: tryOrDefault(
        () => json.weightInKg,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCreated: tryOrDefault(
        () => (json.dateCreated != null ? new Date(json.dateCreated) : null),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: RequestPatientSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('RequestPatientSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequestPatientSummaryViewModelBase implements RequestPatientSummaryProps {
  protected readonly _props: RequestPatientSummaryProps;
  private _json?: RequestPatientSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get dateOfBirth(): Date | null {
    return this._props.dateOfBirth;
  }
  get weightInKg(): number | null {
    return this._props.weightInKg;
  }
  get dateCreated(): Date | null {
    return this._props.dateCreated;
  }

  constructor(props: RequestPatientSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        dateOfBirth: this._props.dateOfBirth,
        weightInKg: this._props.weightInKg,
        dateCreated: this._props.dateCreated,
      };
    }
    return this._json;
  }
}
